"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Flower } from 'lucide-react';
import { Link } from '@/components/common/Link';
import { Button } from '@/components/ui/button';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';
import { cn } from '@/lib/utils';
const steps = [{
  id: '01',
  title: 'Choose Your Date',
  desc: 'Check our availability for your special day. Whether it is a wedding, bridal shower, or corporate pop-up, we recommend booking early to secure your floral experience.',
  icon: Flower
}, {
  id: '02',
  title: 'Select Your Package',
  desc: 'From full-service flower bars to custom bouquet experiences, choose the level of luxury that fits your event size and aesthetic vision perfectly.',
  icon: Flower
}, {
  id: '03',
  title: 'We Bring The Blooms',
  desc: 'Our team arrives with a curated selection of premium, fresh-cut flowers, elegant displays, and all the supplies needed for a seamless interactive experience.',
  icon: Flower
}, {
  id: '04',
  title: 'Create Your Bouquet',
  desc: 'Your guests hand-select their favorite stems and create beautiful, custom bouquets under our expert guidance—a truly photo-worthy moment.',
  icon: Flower
}];
function MethodStep({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4,
    once: false
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("transition-all duration-1000 ease-out flex flex-col md:flex-row items-center gap-12 md:gap-24 py-24 md:py-32", isVisible ? "opacity-100 translate-y-0" : "opacity-30 translate-y-12", isEven ? "md:flex-row" : "md:flex-row-reverse")}>
            {}
            <div className="flex-1 flex justify-center w-full md:w-auto">
                <div className="text-[8rem] leading-none font-serif font-bold [-webkit-text-stroke:2px_var(--color-secondary)] opacity-40 select-none !bg-transparent !text-[rgba(238,99,151,1)] md:text-[14rem]">
                    {step.id}
                </div>
            </div>

            {}
            <div className="flex-1 text-center md:text-left flex flex-col items-center md:items-start w-full md:w-auto">
                <div className="p-5 rounded-lg bg-secondary/10 mb-8 border border-secondary/30 shadow-md shadow-secondary/5">
                    <step.icon className="w-10 h-10 md:w-12 md:h-12 text-secondary" strokeWidth={1} />
                </div>
                <h3 className="text-3xl md:text-5xl font-default font-bold mb-6 tracking-tight text-primary">{step.title}</h3>
                <p className="text-foreground text-lg md:text-xl leading-relaxed max-w-lg font-default">
                    {step.desc}
                </p>
            </div>
        </div>;
}
export default function TheArtOfTheBar() {
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollable = rect.height - windowHeight;
      if (totalScrollable <= 0) {
        setProgress(100);
        return;
      }
      const scrolled = -rect.top;
      let percent = scrolled / totalScrollable * 100;
      percent = Math.max(0, Math.min(100, percent));
      setProgress(percent);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <section data-section-id="260409" id="the-art-of-the-bar" ref={sectionRef} className="bg-background text-foreground relative min-h-screen overflow-hidden border-t border-secondary/30 border-b">
            {}
            <div className="absolute inset-0 z-0 opacity-10 pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1691064/img/botanical_texture_dark.png)] bg-cover bg-fixed bg-center" />

            {}
            <div className="sticky top-0 z-50 w-full h-1.5 bg-background/80 backdrop-blur-md border-b border-border/50">
                <div className="h-full bg-secondary transition-all duration-150 ease-out" style={{
        width: `${progress}%`
      }} />
            </div>

            <div className="container mx-auto px-4 relative z-10 py-24">
                {}
                <div className="text-center mb-20 md:mb-32">
                    <h2 className="text-4xl md:text-6xl font-default font-bold mb-6 tracking-tight text-primary">The Art of the Bar</h2>
                    <p className="text-lg md:text-xl text-foreground max-w-2xl mx-auto font-default">
                        A meticulous four-stage process designed to transform your event into a blooming, interactive masterpiece.
                    </p>
                </div>

                {}
                <div className="flex flex-col max-w-6xl mx-auto">
                    {steps.map((step, i) => <MethodStep key={step.id} step={step} index={i} />)}
                </div>

                {}
                <div className="text-center mt-20 md:mt-32 pb-12">
                    <Button size="lg" asChild className="rounded-lg px-8 h-14 text-lg shadow-lg shadow-primary/20 bg-primary text-primary-foreground hover:bg-accent transition-colors">
                        <Link to="/ContactPage">Start Your Adventure</Link>
                    </Button>
                </div>
            </div>
        </section>;
}